import { useEffect, useRef, useState } from 'react';
import './TransactionPage.css';
import Loader from '../../components/Loader/Loader.jsx';
import { listTransactions } from '../../api/transactionsAPI/transactionsAPI.js';
import { formatTxType } from '../../api/format/format.js';
import Pagination from '../../components/Pagination/Pagination.jsx';
import useLang from '../../hooks/use-lang/useLang.js';

const FILTER_OPTIONS = ['all', 'completed', 'pending', 'failed', 'deposit', 'payment'];

const STATUS_FILTERS = ['completed', 'pending', 'failed'];
const TYPE_FILTERS = ['deposit', 'payment'];
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 6;

function TransactionPage({ onNavigate, onLogout }) {
  const { t } = useLang();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [detailsTransaction, setDetailsTransaction] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const filterRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      const params = { search };
      if (STATUS_FILTERS.includes(filter)) params.status = filter;
      else if (TYPE_FILTERS.includes(filter)) params.type = filter;
      listTransactions(params)
        .then((data) => {
          setTransactions(data);
          setError(null);
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search, filter]);

  useEffect(() => {
    setPage(1);
  }, [search, filter]);

  const totalPages = Math.ceil(transactions.length / PAGE_SIZE);
  const filteredTransactions = transactions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="transactions__page">
      <div className="page__container">
        <div className="transactions__page__header">
          <div className="filter__container" ref={filterRef}>
            <button
              className="transactions__filter-trigger"
              type="button"
              onClick={() => setIsFilterOpen((prev) => !prev)}
              aria-label={t('transactions.filterAria', 'Filter transactions')}
              aria-haspopup="listbox"
              aria-expanded={isFilterOpen}
            >
              <span>{filter === 'all' ? t('common.all') : STATUS_FILTERS.includes(filter) ? t(`common.${filter}`, filter) : t(`common.txType.${filter}`, filter)}</span>
              <svg className="transactions__filter-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isFilterOpen && (
              <ul className="transactions__filter-options" role="listbox">
                {FILTER_OPTIONS.map((value) => (
                  <li key={value} role="option" aria-selected={filter === value}>
                    <button
                      className={filter === value ? 'transactions__filter-option transactions__filter-option--active' : 'transactions__filter-option'}
                      type="button"
                      onClick={() => {
                        setFilter(value);
                        setIsFilterOpen(false);
                      }}
                    >
                      {value === 'all' ? t('common.all') : STATUS_FILTERS.includes(value) ? t(`common.${value}`, value) : t(`common.txType.${value}`, value)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="search__input__container">
            <input
              className="transactions__search"
              type="text"
              placeholder={t('transactions.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t('transactions.searchAria', 'Search transactions')}
            />
          </div>
        </div>

        {loading ? (
          <Loader text={t('transactions.loading')} />
        ) : (
        <div className="transactionsPage__card__container">
          {!loading && error && <p className="transactions__empty">{t('common.error')}: {error}</p>}
          {!loading && !error && filteredTransactions.length === 0 && (
            <p className="transactions__empty">{t('transactions.empty')}</p>
          )}
          {!loading && !error && filteredTransactions.map((transaction) => (
            <div key={transaction.id} className="transactions_card">
              <div className="transaction__info__container">
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.userId')}</span>
                  <span className="transaction__info__value">{transaction.userId}</span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.type')}</span>
                  <span className={`transaction__info__value ${transaction.type === 'deposit' ? 'transaction__type--deposit' : 'transaction__type--payment'}`}>
                    {t(`common.txType.${transaction.type}`, formatTxType(transaction.type))}
                  </span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.amount')}</span>
                  <span className="transaction__info__value">{`$${transaction.amount.toFixed(2)}`}</span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.date')}</span>
                  <span className="transaction__info__value">{transaction.date}</span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('common.status')}</span>
                  <span className={`transaction__info__value ${transaction.status === 'completed' ? 'transaction__status--completed' : transaction.status === 'pending' ? 'transaction__status--pending' : 'transaction__status--failed'}`}>
                    {t(`common.${transaction.status}`, transaction.status)}
                  </span>
                </div>
              </div>

              <div className="transactions__controller__container">
                <button className="transactions__btn transactions__btn--details" type="button" onClick={() => setDetailsTransaction(transaction)}>{t('common.details')}</button>
              </div>
            </div>
          ))}
        </div>
        )}

        {!loading && !error && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>

      {detailsTransaction && (
        <div className="transaction-modal-overlay" onClick={() => setDetailsTransaction(null)}>
          <div className="transaction-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="transaction-modal-title">{t('transactions.detailsTitle')}</h3>
            <div className="transaction-modal-section">
              <div className="transaction__info__container">
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.txId')}</span>
                  <span className="transaction__info__value">#{detailsTransaction.id}</span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.userId')}</span>
                  <span className="transaction__info__value">{detailsTransaction.userId}</span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.type')}</span>
                  <span className={`transaction__info__value ${detailsTransaction.type === 'deposit' ? 'transaction__type--deposit' : 'transaction__type--payment'}`}>
                    {t(`common.txType.${detailsTransaction.type}`, formatTxType(detailsTransaction.type))}
                  </span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.amount')}</span>
                  <span className="transaction__info__value">{`$${detailsTransaction.amount.toFixed(2)}`}</span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('transactions.date')}</span>
                  <span className="transaction__info__value">{detailsTransaction.date}</span>
                </div>
                <div className="transaction__info__row">
                  <span className="transaction__info__label">{t('common.status')}</span>
                  <span className={`transaction__info__value ${detailsTransaction.status === 'completed' ? 'transaction__status--completed' : detailsTransaction.status === 'pending' ? 'transaction__status--pending' : 'transaction__status--failed'}`}>
                    {t(`common.${detailsTransaction.status}`, detailsTransaction.status)}
                  </span>
                </div>
              </div>
            </div>
            <div className="transaction-modal-actions">
              <button className="transactions__btn transactions__btn--details" type="button" onClick={() => setDetailsTransaction(null)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionPage;